/**
 * Schémas Zod — Brokers.
 *
 * Un "broker" est un `app.users` avec `role in ('broker', 'tenant_admin')`,
 * et idéalement un `app.broker_profiles` associé. Les routes courtage
 * exposent une vue agrégée user + profil.
 *
 * - InviteBrokerSchema       : crée user + profil (réutilise le flow invitation auth)
 * - UpdateBrokerProfileSchema: met à jour les champs métier (licence, spécialités, bio…)
 * - BrokerFilterSchema       : filtres pour GET /v1/brokers
 */

import { z } from "zod";

import { DateOnlySchema, EmailSchema, LocaleSchema } from "./common.js";

const BrokerSpecialty = z
  .string()
  .min(2)
  .max(50)
  .regex(/^[a-z][a-z0-9_-]*$/i, "Spécialité invalide");

export const InviteBrokerSchema = z.object({
  email: EmailSchema,
  full_name: z.string().min(2).max(120),
  role: z.enum(["broker", "tenant_admin"]).default("broker"),
  locale: LocaleSchema,
  // Profil pré-rempli (optionnel)
  license_number: z.string().max(50).optional(),
  license_expiry: DateOnlySchema.optional(),
  specialties: z.array(BrokerSpecialty).max(20).optional(),
  message: z.string().max(500).optional(),
});
export type InviteBrokerInput = z.infer<typeof InviteBrokerSchema>;

export const UpdateBrokerProfileSchema = z.object({
  license_number: z.string().max(50).nullable().optional(),
  license_expiry: DateOnlySchema.nullable().optional(),
  specialties: z.array(BrokerSpecialty).max(20).optional(),
  bio: z.string().max(2000).nullable().optional(),
  signature_block: z.string().max(5000).nullable().optional(),
});
export type UpdateBrokerProfileInput = z.infer<typeof UpdateBrokerProfileSchema>;

export const BrokerFilterSchema = z.object({
  role: z.enum(["broker", "tenant_admin", "all"]).default("all"),
  q: z.string().max(100).optional(),
  include_disabled: z.coerce.boolean().default(false),
});
export type BrokerFilter = z.infer<typeof BrokerFilterSchema>;
